import type { Channel, KycConfirmed, KycPerson, KycView } from "../api/types.js";
import { calendarDate, calendarMonth, clock } from "../lib/format.js";
import { ADD_COPY, DISPLAY_ODD } from "./add-payee-model.js";

/**
 * DCTA-176 F46 (docs/design/v3/04-kyc.md §0.2, §2.2; UX spec v3 §F.2 row 25) — the identity-check refresh's words and
 * what its screens show. Pure: no DOM, no network. The flow (`kyc-flow.ts`) drives it; the screens (`ui/kyc.ts`) draw it.
 *
 * Rules held here:
 *  - one name for the thing: the "identity check", and the customer "updates your details" — never KYC, CDD, due
 *    diligence, a link, a password, a PIN or a code (25k);
 *  - what Myinfo returned is read-only and drawn as the government holds it (the address in Myinfo's own lines);
 *  - the card at Check and sign is the STORED payload's one `kyc_declaration` step and nothing else (shown = signed): a
 *    payload of any other shape draws no card at all (`kycCardOf`);
 *  - a date the bank did not send is never printed — no invented month, no guessed review date.
 */
export const KYC_COPY = {
  /** 25a: the flow's title; where the customer is ("step" is never shown). */
  title: "Update your details",
  /** Literal, so the copy is greppable (tools/voice-audit.mjs reads the app's source for "3 of 3"). */
  steps: ["1 of 3", "2 of 3", "3 of 3"] as const,
  of: (n: 1 | 2 | 3): string => KYC_COPY.steps[n - 1] as string,
  /** The mock-up's Check and sign screen title (the card itself says what is signed). */
  signTitle: "Check and sign",
  /** 25b. */
  heroLapsed: "Your identity check is overdue",
  heroDue: "Your identity check is due",
  why: "We check every customer's details from time to time.",
  lastChecked: (month: string): string => `Yours were last checked in ${month}.`,
  /** 25c: the three parts, in order — a real sequence, so numbered. */
  parts: [
    ["Get your details from Myinfo", "Name, NRIC, date of birth and address"],
    ["Add your job and where your money comes from", "Filled in from what we hold"],
    ["Sign with your passkey", "One touch, as for a payment"],
  ] as const,
  /** 25d. */
  safety: "We only ask for this here in the app. We never send a link for it.",
  getMyinfo: "Get my details from Myinfo",
  tryAgain: "Try again",
  talkStart: "Can't use Myinfo? Talk to us",
  demo: "Demo: no real Myinfo data is used.",
  /** 25e: the consent placeholder — it stands where the Singpass app's own screen is in real life. */
  consentTitle: "Share your Myinfo with dctagent.com?",
  consentItems: ["Name", "NRIC or FIN", "Date of birth", "Registered address"] as const,
  share: "Share",
  cancel: "Cancel",
  consentFake: "Your Singpass app · placeholder · demo",
  /** 25f. */
  retrieving: "Getting your details from Myinfo",
  /** 25g. */
  fromMyinfo: "From Myinfo",
  demoFlag: "Demo data",
  readOnly: "From the government's records, so they can't be changed here.",
  fromYou: "From you",
  occupation: "Occupation",
  moneyFrom: "Where your money comes from",
  pickAll: "Pick all that apply.",
  review: "Review",
  talkDetails: "Something wrong? Talk to us",
  /** 25h. */
  cardTitle: "Update your details",
  moneyFromShort: "Money from",
  sign: "Sign · update details",
  notNow: "Not now",
  /** 25i. */
  declined: "Nothing was shared from Myinfo.",
  unavailable: { lead: "Myinfo isn't answering right now.", rest: "Try again in a few minutes, or talk to us." },
  pickOne: "Pick at least one.",
  expired: "This took too long, so nothing was saved. Start again.",
  /** 25j. */
  doneTitle: "Your details are up to date",
  doneAgain: "Payments work again.",
  holds: "What the bank holds",
  checked: "Checked",
  today: (time: string): string => `Today, ${time}`,
  nextCheck: "Next check",
  done: "Done",
  /** §2.8: Myinfo holds no address for some pass holders. */
  notInMyinfo: "Not in Myinfo",
  /**
   * New (DCTA-176 FE, flagged for ux-designer): the occupation is a text field until the P1 list sheet exists — its two
   * problems. The second is 17's accepted line for the same display rule (ADD_COPY.nicknameOdd).
   */
  occupationEmpty: "Enter your occupation.",
  occupationOdd: ADD_COPY.nicknameOdd,
  occupationLong: "Use 60 characters or fewer.",
  /** New (DCTA-176 FE, flagged): the bank could not be reached — 17's accepted "Couldn't reach the bank" in the refresh's terms (25i "nothing was saved"). */
  noBank: { lead: "Couldn't reach the bank", rest: "Nothing was saved." },
} as const;

/** 25g: where money comes from — the bank's keys (B58 `options.source_of_funds`) in the deck's words. */
export const FUNDS: Readonly<Record<string, string>> = {
  salary: "Salary",
  business: "My business",
  investments: "Investments",
  savings: "Savings",
  pension: "Pension or CPF",
  family: "Family",
};

export interface Row {
  readonly k: string;
  /** One or more lines (an address is Myinfo's own lines); text, never markup. */
  readonly v: readonly string[];
}

/** 25b: the line beside the mark — overdue (money stopped) or due — and why, with the month the bank last checked. */
export function startHero(status: string, lastChecked: string | undefined): { readonly title: string; readonly lead: string } {
  const month = lastChecked === undefined ? undefined : calendarMonth(lastChecked);
  const dated = month !== undefined && month !== lastChecked;
  return {
    // Due only while "refresh_due"; anything else (restricted, unreadable) has stopped money: overdue.
    title: status === "refresh_due" ? KYC_COPY.heroDue : KYC_COPY.heroLapsed,
    lead: dated ? `${KYC_COPY.why} ${KYC_COPY.lastChecked(month)}` : KYC_COPY.why,
  };
}

/** Myinfo's four rows, read-only, in Myinfo's order (25g). */
export function personRows(person: Omit<KycPerson, "source"> & { readonly source?: string }): Row[] {
  const address = person.registered_address;
  return [
    { k: "Name", v: [person.name] },
    { k: "NRIC or FIN", v: [person.uinfin_masked] },
    { k: "Date of birth", v: [calendarDate(person.dob)] },
    { k: "Registered address", v: address === undefined || address.length === 0 ? [KYC_COPY.notInMyinfo] : [...address] },
  ];
}

/** The bank's choices for where money comes from, in its order — only those the deck has words for. */
export function fundsOffered(options: readonly string[]): { readonly key: string; readonly label: string }[] {
  return options.flatMap((key) => {
    const label = FUNDS[key];
    return label === undefined ? [] : [{ key, label }];
  });
}

/** "Salary, Savings" — the signed sources in the bank's order; one the deck has no words for is printed as signed. */
export function fundsLine(keys: readonly string[]): string {
  return keys.map((k) => FUNDS[k] ?? k).join(", ");
}

/** What would stop the bank taking the occupation — or nothing. UX only: the bank is the check (zod on the route). */
export function occupationProblem(typed: string): string | undefined {
  const t = typed.trim();
  if (t === "") return KYC_COPY.occupationEmpty;
  if (t.length > 60) return KYC_COPY.occupationLong;
  if (DISPLAY_ODD.test(t)) return KYC_COPY.occupationOdd;
  return undefined;
}

/** The card at Check and sign: the stored payload's one `kyc_declaration` step, as signed. */
export interface KycCard {
  readonly bundleId: string;
  readonly person: KycPerson;
  readonly occupation: string;
  readonly funds: readonly string[];
  readonly declaration: string;
  readonly nextReviewDue: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const isRecord = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
const isText = (v: unknown): v is string => typeof v === "string" && v !== "";
const isTexts = (v: unknown): v is string[] => Array.isArray(v) && v.length > 0 && v.every(isText);

/**
 * The card, from `GET /v1/bundles/{id}` and nothing else — or undefined when the payload is not exactly ONE well-formed
 * `kyc_declaration` made on the refresh screen (B58). A shape the screen cannot read is never drawn half: no card, no Sign.
 */
export function kycCardOf(bundle: unknown): KycCard | undefined {
  if (!isRecord(bundle) || !isText(bundle["bundle_id"])) return undefined;
  const payload = bundle["signing_payload"];
  if (!isRecord(payload) || !Array.isArray(payload["steps"]) || payload["steps"].length !== 1) return undefined;
  const origin = payload["origin"];
  if (!isRecord(origin) || origin["kind"] !== "bank_surface" || origin["surface"] !== "kyc_refresh") return undefined;
  const step: unknown = payload["steps"][0];
  if (!isRecord(step) || step["type"] !== "kyc_declaration") return undefined;
  const person = step["person"];
  const profile = step["profile"];
  if (!isRecord(person) || !isRecord(profile)) return undefined;
  const address = person["registered_address"];
  if (!isText(person["name"]) || !isText(person["uinfin_masked"]) || !isText(person["dob"]) || !DATE.test(person["dob"])) return undefined;
  if (address !== undefined && !isTexts(address)) return undefined;
  if (!isText(profile["occupation"]) || !isTexts(profile["source_of_funds"])) return undefined;
  if (!isText(step["declaration"]) || !isText(step["next_review_due"]) || !DATE.test(step["next_review_due"])) return undefined;
  if (!isText(payload["created_at"]) || !isText(payload["expires_at"])) return undefined;
  return {
    bundleId: bundle["bundle_id"],
    person: {
      name: person["name"],
      uinfin_masked: person["uinfin_masked"],
      dob: person["dob"],
      ...(address === undefined ? {} : { registered_address: address }),
      source: isText(person["source"]) ? person["source"] : "",
    },
    occupation: profile["occupation"],
    funds: profile["source_of_funds"],
    declaration: step["declaration"],
    nextReviewDue: step["next_review_due"],
    createdAt: payload["created_at"],
    expiresAt: payload["expires_at"],
  };
}

/** 25h: every detail being declared — the Myinfo rows, then the customer's own — exactly as signed. */
export function cardRows(card: KycCard): Row[] {
  return [...personRows(card.person), { k: KYC_COPY.occupation, v: [card.occupation] }, { k: KYC_COPY.moneyFromShort, v: [fundsLine(card.funds)] }];
}

/** The declaration above Sign: its first sentence in bold, the rest after it — the bank's text, split, never reworded. */
export function declarationParts(text: string): { readonly lead: string; readonly rest: string } {
  const end = text.search(/[.!?](\s|$)/);
  return end < 0 ? { lead: text, rest: "" } : { lead: text.slice(0, end + 1), rest: text.slice(end + 1) };
}

/**
 * DCTA-198 (code review DCTA-193 P2-F1 / P2-B3): how "Ask again" sends the refused words back — the way they arrived, as the
 * bank's `reask.channel` says; spoken when it does not say (on `source: reask` the bank reads its own record regardless).
 * Never a hard-coded "typed": that turned spoken words into typed ones on the record.
 */
export function reaskChannel(reask: KycConfirmed["reask"]): Channel {
  return reask?.channel ?? "voice";
}

export interface DoneModel {
  readonly title: string;
  /** 25j "Payments work again." — only when the bank sent 24d (money had stopped). */
  readonly again: boolean;
  readonly rows: readonly Row[];
}

/**
 * 25j: what the bank now holds — from the confirm's answer (`kyc`), the payload signed and the bank's time of the confirm.
 * Already up to date (409 at the start): the title alone.
 */
export function doneModel(kyc: KycConfirmed | undefined, card: KycCard | undefined, at: string | undefined): DoneModel {
  const rows: Row[] = [];
  if (at !== undefined) rows.push({ k: KYC_COPY.checked, v: [KYC_COPY.today(clock(at))] });
  if (kyc !== undefined) rows.push({ k: KYC_COPY.nextCheck, v: [calendarMonth(kyc.next_review_due)] });
  if (card !== undefined) rows.push({ k: KYC_COPY.occupation, v: [card.occupation] }, { k: KYC_COPY.moneyFromShort, v: [fundsLine(card.funds)] });
  return { title: KYC_COPY.doneTitle, again: kyc?.message !== undefined && kyc.message !== "", rows };
}

/** 26a: the Home banner's words while the check is due — the date in its own span (it never breaks across lines). */
export const DUE_COPY = {
  title: "Your identity check is due",
  until: (date: string): readonly [string, string, string] => ["Payments keep working until ", date, "."],
  update: "Update now",
} as const;

/**
 * P1-5 (DCTA-192, polish audit): the Home banner's words while the check has lapsed — the due banner's component, its words
 * for a customer whose payments are stopped (Ng had no banner while Rahim, only due, had one).
 */
export const LAPSED_COPY = {
  title: "Your identity check has lapsed",
  line: "Payments are stopped until you update it.",
  update: "Update now",
} as const;

/** 24c: the bank's own short line while money cannot move (B56) — Me's row says the same. */
const LAPSED_LINE = "Money can't move until your details are updated.";

/**
 * F48: Me's "Your identity check" row — only where the refresh exists (demo mode) and only while the check is not up to
 * date: due → until when payments keep working (26a's words); lapsed or unreadable → the bank's short line (24c).
 */
export function kycRow(kyc: KycView | undefined, demo: boolean): { readonly sub: string } | undefined {
  if (kyc === undefined || !demo || kyc.status === "verified") return undefined;
  if (kyc.status === "refresh_due") return { sub: kyc.update_by === undefined ? DUE_COPY.title : DUE_COPY.until(calendarDate(kyc.update_by)).join("") };
  return { sub: LAPSED_LINE };
}
